import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
// services
import { MessageService } from "../../../../services/message.service";
import { MasterService } from "../../../../services/master.service";
import * as languageLibrary from '../../../../services/language'
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
//

@Component({
    selector: 'collections-setup',
    templateUrl: './collections-setup.component.html',
    styleUrls: ['./collections-setup.component.scss'],
    standalone: false
})
export class CollectionsSetupComponent implements OnInit, OnDestroy {
    // *******************
    // ** variables ******
    // *******************
    //
    // save the language
    @Input() language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    @Input() entityType: string

    @Input() entityId: number

    protected paymentAllocationData = [];

    protected paymentAllocationOrderTemplate: Array<Object> = [
        { 'id': 1, 'name': 'Initial Down', 'OrderNo': 1, 'flag': true, 'draggable': false },
        { 'id': 2, 'name': 'Deferred Down', 'OrderNo': 2, 'flag': false, 'draggable': true },
        { 'id': 3, 'name': 'MV Loan', 'OrderNo': 3, 'flag': false, 'draggable': true },
        { 'id': 4, 'name': 'CPI', 'OrderNo': 4, 'flag': false, 'draggable': true },
        { 'id': 5, 'name': 'After-Sale Charges', 'OrderNo': 5, 'flag': false, 'draggable': true },
        { 'id': 6, 'name': 'Drive-Out Price', 'OrderNo': 6, 'flag': false, 'draggable': true },
        { 'id': 7, 'name': 'Bounce', 'OrderNo': 7, 'flag': false, 'draggable': true },
        { 'id': 8, 'name': 'Late Fee', 'OrderNo': 8, 'flag': false, 'draggable': true }
    ]

    protected loading: boolean = false

    protected expanded: boolean = true

    protected collectionSetup: Object;

    protected form: FormGroup | null = null

    private subscriptions: Subscription[] = []

    // set all words
    protected words = languageLibrary.language

    // *******************
    // ** functions ******
    // *******************

    constructor(private ms: MessageService, private master: MasterService, private fb: FormBuilder, private route: ActivatedRoute) {
        this.entityType = this.route.snapshot.params['type']
        this.entityId = parseInt(this.route.snapshot.params['id'])

        if (isNaN(this.entityId) || this.entityId == 0) {
            this.entityId = null
        }

        this.form = this.fb.group({
            id: new FormControl<number>(0),
            dealershipId: this.entityType === 'dealership' ? new FormControl<number | null>(this.entityId) : new FormControl<string>(''),
            lotId: this.entityType === 'lot' ? new FormControl<number | null>(this.entityId) : new FormControl<string>(''),
            receiptStartingNumber: new FormControl<number>(1000, [Validators.required, Validators.maxLength(10), Validators.max(2147483647)]),
            paymentAllocationOrder: new FormControl<string | null>(null),
            defaultCollectionMethod: new FormControl<string>('Scheduled Installment Earnings', [Validators.required]),
            enableLateFees: new FormControl<boolean>(true),
            allowWaiveLateFee: new FormControl<boolean>(false),
            lateFeePercent: new FormControl<number>(5),
            lateFeeMethod: new FormControl<string>('scheduled', [Validators.required]),
            amountLateTolerance: new FormControl<number>(0),
            daysLateTolerance: new FormControl<number>(0)
        })

        this.subscriptions.push(
            this.form.get('enableLateFees').valueChanges.subscribe((newValue) => {
                this.modifyLateFeeState()
            })
        )
    }

    ngOnInit() {
        this.getCollectionSetup()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }


    private modifyLateFeeState() {
        const enabled = this.form.get('enableLateFees').value

        if (enabled) {
            this.form.get('allowWaiveLateFee').enable()
            this.form.get('lateFeePercent').enable()
            this.form.get('lateFeeMethod').enable()
            this.form.get('amountLateTolerance').enable()
            this.form.get('daysLateTolerance').enable()
            return
        }

        this.form.get('allowWaiveLateFee').disable()
        this.form.get('lateFeePercent').disable()
        this.form.get('lateFeeMethod').disable()
        this.form.get('amountLateTolerance').disable()
        this.form.get('daysLateTolerance').disable()
    }

    private getCollectionSetup() {
        this.paymentAllocationData = this.paymentAllocationOrderTemplate
        if (isNaN(this.entityId) || this.entityId == 0) {
            return
        }

        this.master.get(`lots/${this.entityId}/collectionSetup`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.collectionSetup = res.data.collectionSetup
            if (!this.collectionSetup) {
                return
            }

            this.setInformationValues(this.collectionSetup)
        })

    }

    private setInformationValues(collectionSetupData: Object) {
        this.paymentAllocationData = JSON.parse(collectionSetupData['paymentAllocationOrder'])
        this.form.setValue({
            id: collectionSetupData['id'],
            lotId: collectionSetupData['lotId'],
            dealershipId: collectionSetupData['dealershipId'],
            receiptStartingNumber: collectionSetupData['receiptStartingNumber'],
            paymentAllocationOrder: collectionSetupData['paymentAllocationOrder'],
            defaultCollectionMethod: collectionSetupData['defaultCollectionMethod'],
            enableLateFees: collectionSetupData['enableLateFees'],
            allowWaiveLateFee: collectionSetupData['allowWaiveLateFee'],
            lateFeePercent: collectionSetupData['lateFeePercent'],
            lateFeeMethod: collectionSetupData['lateFeeMethod'],
            amountLateTolerance: collectionSetupData['amountLateTolerance'],
            daysLateTolerance: collectionSetupData['daysLateTolerance']
        });
    }

    protected save() {
        if (this.form.invalid) {
            return
        }

        this.form.get('paymentAllocationOrder').setValue(JSON.stringify(this.paymentAllocationData))

        if (!this.form.controls['id'].value) {
            this.createCollectionSetup()
            return
        }

        this.modifyCollectionSetup()

    }

    private modifyCollectionSetup() {
        this.master.put(`lots/${this.entityId}/collectionSetup/${this.form.get('id').value}`, { collectionSetupData: this.form.value }, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

        })
    }

    private createCollectionSetup() {
        this.loading = true

        this.form.get('lotId').setValue(this.entityType === 'lot' ? this.entityId : null)
        this.form.get('dealershipId').setValue(this.entityType === 'dealership' ? this.entityId : null)

        this.master.post(`lots/${this.entityId}/collectionSetup`, { collectionSetupData: this.form.value }, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.form.get('id').setValue(res.data.collectionSetup['id'])

        })
    }

    private computeTerms() {
        const lotId = parseInt(this.form.get('lotId').value)
        if (!lotId || (isNaN(lotId)) || (lotId <= 0)) {
            return
        }

        this.master.post(`automation/terms/compute`, { lotId: lotId }, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            
        })
    }

    public resetDefaultPaymentAllocationOrder() {
        this.paymentAllocationData = this.paymentAllocationOrderTemplate.sort((a, b) => a['OrderNo'] - b['OrderNo'])
    }

    public drop(event: CdkDragDrop<string[]>) {
        if (event.currentIndex == 0 || event.previousIndex == event.currentIndex) {
            return
        }
        moveItemInArray(this.paymentAllocationData, event.previousIndex, event.currentIndex);
    }

}
